import React, { useMemo } from "react";
import {
  Box,
  Stack,
  TextField,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  IconButton,
  Chip,
  Button,
  Tooltip,
  Skeleton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import StaffSearchSelect from "../../Components/StaffSearchSelect";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  useGetallLocationQuery,
  useGetTeamQuery,
} from "../../Api/attoDeskApi";
import useUser from "../../Core/hook/useUser";

/**
 * Filters bar for the "Today" timetable.
 *
 * State model:
 *   - "Server filters" (location, mode, staff) are sent to /timetable/week
 *     and re-narrow the whole payload. Cheap on the DB, kills roundtrips.
 *   - "Client filters" (search, has-spots, makeup-only, has-outstanding)
 *     are cosmetic — they hide cards from the already-fetched payload so
 *     switching them doesn't cost a network hop. Applied in TodayPage.
 *
 * That split matches the spec: "server-side filtering where necessary".
 */

export type TodayFilters = {
  // Server filters
  location_id?: number | "";
  mode?: "online" | "in_person" | "";
  staff_id?: number | "";
  // Client filters
  search: string;
  makeupOnly: boolean;
  hasSpotsOnly: boolean;
  outstandingOnly: boolean;
};

export const EMPTY_FILTERS: TodayFilters = {
  location_id: "",
  mode: "",
  staff_id: "",
  search: "",
  makeupOnly: false,
  hasSpotsOnly: false,
  outstandingOnly: false,
};

type Props = {
  filters: TodayFilters;
  onChange: (next: TodayFilters) => void;
  canSeeFinance: boolean;
};

const FilterBar: React.FC<Props> = ({ filters, onChange, canSeeFinance }) => {
  // Backend now scopes /timetable/week by staff_id for non-admins, so the
  // Staff picker only has meaning for admin/ops accounts that can see the
  // whole school. Hiding it for teachers avoids a dropdown whose only
  // effect is "narrow from my one row to my one row".
  const { isSystemAdmin } = useUser();
  // Locations & staff are stable lookups — safe to keep in cache.
  const { data: locationData, isLoading: locationLoading } = useGetallLocationQuery({ searchText: "" });
  const { data: staffData, isLoading: staffLoading } = useGetTeamQuery(
    { searchText: "", id: "" },
    { skip: !isSystemAdmin },
  );

  const locations = useMemo(
    () => ((locationData as any)?.data ?? []) as Array<{ id: number; location_name: string }>,
    [locationData]
  );
  const staffList = useMemo(
    () => ((staffData as any)?.data ?? []) as Array<{ id: number; teamFirstName?: string; teamLastName?: string }>,
    [staffData]
  );

  const patch = (p: Partial<TodayFilters>) => onChange({ ...filters, ...p });

  const activeChips: Array<{ key: keyof TodayFilters; label: string }> = [];
  if (filters.location_id) {
    const loc = locations.find((l) => l.id === filters.location_id);
    activeChips.push({ key: "location_id", label: `Location: ${loc?.location_name ?? filters.location_id}` });
  }
  if (filters.mode)             activeChips.push({ key: "mode",             label: `Mode: ${filters.mode === "online" ? "Online" : "In-Person"}` });
  if (filters.staff_id) {
    const s = staffList.find((t) => t.id === filters.staff_id);
    const name = s ? `${s.teamFirstName ?? ""} ${s.teamLastName ?? ""}`.trim() : String(filters.staff_id);
    activeChips.push({ key: "staff_id", label: `Staff: ${name}` });
  }
  if (filters.search.trim())    activeChips.push({ key: "search",           label: `Search: "${filters.search.trim()}"` });
  if (filters.makeupOnly)       activeChips.push({ key: "makeupOnly",       label: "Makeup only" });
  if (filters.hasSpotsOnly)     activeChips.push({ key: "hasSpotsOnly",     label: "Has spots" });
  if (filters.outstandingOnly)  activeChips.push({ key: "outstandingOnly",  label: "Outstanding invoice" });

  const clearOne = (key: keyof TodayFilters) => {
    const resetVal: any = typeof filters[key] === "boolean" ? false : "";
    patch({ [key]: resetVal } as any);
  };

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "12px",
        border: "1px solid rgba(0,0,0,0.06)",
        p: 1.25,
        mb: 2,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.25}
        alignItems={{ xs: "stretch", md: "center" }}
        flexWrap="wrap"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary", flexShrink: 0 }}>
          <FilterAltOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>

        <TextField
          size="small"
          placeholder="Search student or staff…"
          value={filters.search}
          onChange={(e) => patch({ search: e.target.value })}
          sx={{ minWidth: 200, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              </InputAdornment>
            ),
            endAdornment: filters.search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => patch({ search: "" })}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />

        {locationLoading ? (
          <Skeleton variant="rounded" width={160} height={36} />
        ) : (
          <TextField
            select size="small" label="Location"
            value={filters.location_id ?? ""}
            onChange={(e) => patch({ location_id: e.target.value ? Number(e.target.value) : "" })}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All Locations</MenuItem>
            {locations.map((l) => (
              <MenuItem key={l.id} value={l.id}>{l.location_name}</MenuItem>
            ))}
          </TextField>
        )}

        <ToggleButtonGroup
          size="small"
          exclusive
          value={filters.mode || ""}
          onChange={(_, v) => patch({ mode: (v ?? "") as TodayFilters["mode"] })}
          sx={{ height: 36 }}
        >
          <ToggleButton value="" sx={{ textTransform: "none", px: 1.25 }}>All</ToggleButton>
          <ToggleButton value="online" sx={{ textTransform: "none", px: 1.25 }}>Online</ToggleButton>
          <ToggleButton value="in_person" sx={{ textTransform: "none", px: 1.25 }}>In-Person</ToggleButton>
        </ToggleButtonGroup>

        {isSystemAdmin && (
          staffLoading ? (
            <Skeleton variant="rounded" width={180} height={36} />
          ) : (
            <StaffSearchSelect
              label="Staff"
              value={filters.staff_id ? String(filters.staff_id) : ""}
              onChange={(v) => patch({ staff_id: v ? Number(v) : "" })}
              staff={staffList as any}
              minWidth={200}
            />
          )
        )}

        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Show only classes with open spots" arrow>
            <ToggleButton
              size="small"
              value="hasSpots"
              selected={filters.hasSpotsOnly}
              onChange={() => patch({ hasSpotsOnly: !filters.hasSpotsOnly })}
              sx={{ textTransform: "none", px: 1, height: 36, gap: 0.5 }}
            >
              <EventAvailableOutlinedIcon sx={{ fontSize: 16 }} /> Spots
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Show only makeup classes" arrow>
            <ToggleButton
              size="small"
              value="makeupOnly"
              selected={filters.makeupOnly}
              onChange={() => patch({ makeupOnly: !filters.makeupOnly })}
              sx={{ textTransform: "none", px: 1, height: 36, gap: 0.5 }}
            >
              <AutorenewOutlinedIcon sx={{ fontSize: 16 }} /> Makeup
            </ToggleButton>
          </Tooltip>
          {/* The Outstanding toggle stays visible for non-finance users so
              they can still hide unaffected classes; only the tooltip
              amounts are gated (Phase 7). */}
          <Tooltip title={canSeeFinance ? "Show only classes with a student holding an unpaid invoice" : "Show only flagged accounts"} arrow>
            <ToggleButton
              size="small"
              value="outstandingOnly"
              selected={filters.outstandingOnly}
              onChange={() => patch({ outstandingOnly: !filters.outstandingOnly })}
              sx={{ textTransform: "none", px: 1, height: 36, gap: 0.5 }}
            >
              <WarningAmberIcon sx={{ fontSize: 16 }} /> Outstanding
            </ToggleButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* ─── Active-filter chips + Clear All ─────────────────────── */}
      {activeChips.length > 0 && (
        <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, flexWrap: "wrap", rowGap: 0.75 }} alignItems="center">
          {activeChips.map((c) => (
            <Chip
              key={c.key}
              label={c.label}
              size="small"
              onDelete={() => clearOne(c.key)}
              sx={{ fontSize: 11.5 }}
            />
          ))}
          <Button
            size="small"
            variant="text"
            onClick={() => onChange(EMPTY_FILTERS)}
            sx={{ textTransform: "none", fontSize: 11.5, ml: 0.5 }}
          >
            Clear All
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default FilterBar;
